import { Alert, Button, Form, Upload } from "antd";
import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

/* -------------------------------------------------------------------------- */
/*                            🔹 Utility Functions                            */
/* -------------------------------------------------------------------------- */

const normalizeAcceptedTypes = (acceptedFileTypes: string | string[]): string => {
  if (Array.isArray(acceptedFileTypes)) {
    return acceptedFileTypes.map((t) => t.trim().toLowerCase()).join(",");
  }
  return acceptedFileTypes;
};

const extractFileExtension = (fileName: string): string =>
  fileName.substring(fileName.lastIndexOf(".")).toLowerCase();

const createBlobFromBase64 = (base64Data: string, contentType: string): Blob => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
};

const getContentTypeFromExtension = (ext: string): string => {
  const types: Record<string, string> = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    zip: "application/zip",
  };
  return types[ext] ?? "application/octet-stream";
};

/* -------------------------------------------------------------------------- */
/*                            🔹 Helper Functions                             */
/* -------------------------------------------------------------------------- */

const sanitizeRules = (rules: any[], label: string, name: string) =>
  Array.isArray(rules)
    ? rules.map((rule) => {
      const clean: any = {};
      for (const key of Object.keys(rule)) {
        let value = rule[key];
        if (value === "true") value = true;
        if (value === "false") value = false;
        if (value !== null && value !== undefined) clean[key] = value;
      }
      if (clean.required && !clean.message)
        clean.message = `${label ?? name} is required.`;
      return clean;
    })
    : [];

const formatFileList = (files?: { name: string; path: string; id?: string }[]) =>
  (files ?? []).map((f, i) => ({
    uid: `${f.id ?? f.name}-${i}`,
    name: f.name,
    status: "done",
    url: f.path,
    id: f.id ?? "",
  }));

/* -------------------------------------------------------------------------- */
/*                         🔹 File Download Handler                           */
/* -------------------------------------------------------------------------- */

const downloadFile = async (file: any) => {
  try {
    const docPath = file.path ?? file.url;
    if (!docPath) return console.error("Missing docPath for file", file);

    const response = await axios.post(
      "/api/Documents/downloadByPath",
      { docPath },
      { headers: { "Content-Type": "application/json" } }
    );

    const base64Data = response.data?.data;
    if (!base64Data) {
      console.error("No base64 data found:", response.data);
      return;
    }

    const ext = file.name?.split(".").pop()?.toLowerCase() ?? "";
    const blob = createBlobFromBase64(base64Data, getContentTypeFromExtension(ext));

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name ?? "downloaded-file";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Download failed:", err);
  }
};

const openFile = (file: any) => {
  const docPath = file.path ?? file.url;

  if (!docPath) {
    console.error("Missing docPath for file", file);
    return;
  }

  window.open(docPath, "_blank");
};

/* -------------------------------------------------------------------------- */
/*                         🔹 Upload Input Component                          */
/* -------------------------------------------------------------------------- */

const UploadInput = ({
  max,
  fileList,
  onChange,
  beforeUpload,
  onPreview,
  acceptedTypes,
  onRemove,
  showUploadList
}: any) => (
  <>
    <Upload
      maxCount={max ?? undefined}
      multiple={max !== 1}
      fileList={fileList}
      accept={acceptedTypes}
      onChange={onChange}
      beforeUpload={beforeUpload}
      onPreview={onPreview}
      onRemove={onRemove}
      showUploadList={showUploadList}
    >
      <Button icon={<UploadOutlined />}>
        {max > 0 ? `Upload (Max: ${max})` : "Upload"}
      </Button>
    </Upload>
    <label style={{
      marginTop: 4,
      wordBreak: "break-word",
      whiteSpace: "normal"
    }}>({acceptedTypes})</label>
  </>
);

/* -------------------------------------------------------------------------- */
/*                           🔹 Dragger Component                             */
/* -------------------------------------------------------------------------- */

const DraggerUpload = ({
  max,
  fileList,
  acceptedTypes,
  onChange,
  beforeUpload,
  onRemove,
  onDrop,
}: any) => (
  <Dragger
    maxCount={max ?? undefined}
    multiple={max !== 1}
    fileList={fileList}
    accept={acceptedTypes}
    onChange={onChange}
    beforeUpload={beforeUpload}
    onRemove={onRemove}
    onDrop={onDrop}
  >
    <p className="ant-upload-drag-icon">
      <InboxOutlined />
    </p>
    <p className="ant-upload-text">Click or drag file to this area to upload</p>
    <p className="ant-upload-hint">
      Support for single or bulk upload. Avoid confidential or banned files.
    </p>
    {max && <p>Maximum {max} {max === 1 ? "file" : "files"} allowed.</p>}
    <p>({acceptedTypes})</p>
  </Dragger>
);

/* -------------------------------------------------------------------------- */
/*                        🔹 Main File Upload Component                        */
/* -------------------------------------------------------------------------- */

const FormFileUpload = ({
  type = "file",
  label = "",
  required = false,
  name = "",
  rules = undefined,
  widthSize = "large",
  form,
  acceptedFileTypes = [],
  max = 0,
  filename,
  resetUpload,
  maxFileSize,
  totalMaxFilesSize,
  ...rest
}: any) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const acceptedTypes = useMemo(
    () => normalizeAcceptedTypes(acceptedFileTypes),
    [acceptedFileTypes]
  );

  useEffect(() => setFileList([]), [resetUpload]);

  useEffect(() => {
    if (filename) {
      try {
        const parsed =
          typeof filename === "string" ? JSON.parse(filename) : filename;
        const newList = formatFileList(parsed);
        setFileList(newList);
        form.setFieldValue(name, newList);
      } catch (err) {
        console.error("Invalid filename:", err);
      }
    }
  }, [filename, form, name]);

  const handleFileChange = useCallback(
    ({ fileList: newFileList }: any) => {
      setFileList(newFileList);
      form.setFieldValue(name, newFileList);
    },
    [form, name]
  );

  const handleRemove = useCallback(
    (file: any) => {
      setAlertMessage("");
      const updatedList = fileList.filter((f) => f.uid !== file.uid);
      setFileList(updatedList);
      form.setFieldValue(name, updatedList);
    },
    [fileList, form, name]
  );

  const beforeUpload = useCallback(
    (file: File) => {
      const ext = extractFileExtension(file.name);
      const allowed = acceptedTypes.split(",").includes(ext);
      if (!allowed) {
        setAlertMessage(`Only ${acceptedTypes} files are allowed.`);
        return Upload.LIST_IGNORE;
      }
      if (max !== 1 && fileList.length >= max) {
        setAlertMessage(`You can only upload a maximum of ${max} files.`);
        return Upload.LIST_IGNORE;
      }

      if (maxFileSize && file.size > maxFileSize * 1024 * 1024) {
        setAlertMessage(
          `File ${file.name} exceeds the maximum size of ${maxFileSize} MB.`
        );
        return Upload.LIST_IGNORE;
      }

      if (totalMaxFilesSize) {
        const totalSize =
          fileList.reduce((sum, f) => sum + (f.size || 0), 0) + file.size;

        if (totalSize > totalMaxFilesSize * 1024 * 1024) {
          setAlertMessage(
            `Total file size exceeds the maximum of ${totalMaxFilesSize} MB.`
          );
          return Upload.LIST_IGNORE;
        }
      }

      return false;
    },
    [acceptedTypes, fileList.length, max]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      const invalidFiles = Array.from(e.dataTransfer.files).filter(
        (f) => !acceptedTypes.split(",").includes(extractFileExtension(f.name))
      );
      if (invalidFiles.length > 0) {
        alert(`Invalid files. Allowed: ${acceptedTypes}`);
      }
    },
    [acceptedTypes]
  );

  const finalRules = useMemo(
    () => sanitizeRules(rules ?? [], label, name),
    [rules, label, name]
  );

  const normFile = (e: any) => (Array.isArray(e) ? e : e?.fileList);

  const UploadComponent =
    type.toLowerCase() === "file" ? UploadInput : DraggerUpload;

  return (
    <>
      {alertMessage && <Alert message={alertMessage} type="warning" showIcon />}
      <Form.Item
        label={label}
        name={name}
        rules={finalRules}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <UploadComponent
          {...rest}
          max={max}
          fileList={fileList}
          acceptedTypes={acceptedTypes}
          onChange={handleFileChange}
          beforeUpload={beforeUpload}
          onRemove={handleRemove}
          onPreview={openFile}
          //onPreview={downloadFile}
          onDrop={handleDrop}
          showUploadList={{
              showRemoveIcon: !rest?.disabled
          }}
        />
      </Form.Item>
    </>
  );
};

export default FormFileUpload;
